/*
INTELLECTUAL PROPERTY NOTICE
© Kaleida Ltd and/or Orbit Tickets Ltd. All rights reserved.
This file contains a combination of:
- Protected Intellectual Property owned by Kaleida Ltd; and
- Custom / Included Intellectual Property owned by the Customer,
and forms part of an application developed by Kaleida within the Platform.
Protected Intellectual Property may only be used as incorporated within applications developed by Kaleida within the Platform and must not be used, reproduced, adapted, or incorporated into any application developed by or on behalf of the Customer (being any application not developed by Kaleida), whether or not such application forms part of the Platform.
Custom / Included Intellectual Property may be used, reproduced, adapted, and incorporated into any application, system, or product developed by or on behalf of the Customer, whether or not such application forms part of the Platform, subject always to the terms of the agreement and provided that such use does not include or rely upon any Protected Intellectual Property of Kaleida Ltd.
For the avoidance of doubt, where separation between Protected Intellectual Property and Custom / Included Intellectual Property is unclear, this file shall be treated as containing Protected Intellectual Property.
*/

// IP-NOTICE: MIXED
import { Component, Injector, isDevMode, OnChanges, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { AsideService } from 'app/layout/components/aside-container/aside.service';
import { TimeProviderCreateUpdateComponent } from 'app/modules/configuration/time-provider/time-provider-create-update/time-provider-create-update.component';
import { TimeModel } from 'app/modules/configuration/time/time.model';
import { TimeService } from 'app/modules/configuration/time/time.service';
import { UsersService } from 'app/modules/configuration/users/user-management/services/users.service';
import { ExternalRaiseDefectFormComponent } from 'app/modules/external-raise-defect/external-raise-defect-form.component';
import { ComponentItem } from 'app/shared/base/aside/component-item';
import { SystemDesignType } from 'app/shared/models/system-design-type';
import { SnackBarService } from 'app/shared/services/snack-bar.service';
import { environment } from 'environments/environment';
import { filter } from 'rxjs';

@Component({
    selector: 'side-menu',
    templateUrl: './side-menu.component.html',
    styleUrls: ['./side-menu.component.scss'],
    standalone: false
})
export class SideMenuComponent implements OnInit, OnChanges, OnDestroy {
  appVersion = '0.0.0.0';

  public timeModel: TimeModel;

  customerId: number = null;
  projectId: number = null;
  systemDesignVisible = false;


  // Configuration Getters
  //#region
  get showConfigurationMenu(): boolean {
    return this.showSuperUserOptions && this.usersService.userPermissions.action.configurationMenu.isEnabled;
  }

  get showSystemMenu(): boolean {
    //return this.usersService.userPermissions.action.systemMenu.isEnabled && environment.allowSystemMenu;
    return this.showSuperUserOptions && environment.allowSystemMenu;
  }

  get showSandboxMenu(): boolean{
    return this.showSuperUserOptions && environment.allowSandboxMenu;
  }

  get showSubmitDefect(): boolean {
    return this.usersService.userPermissions.action.systemMenu.submitDefect;
  }
  get showViewDatabaseAudit(): boolean {
    return this.usersService.userPermissions.action.configurationMenu.accessDatabaseAudit;
  }

  get showSuperUserOptions():boolean{
    return this.usersService.userPermissions.isSuperUser
  }
  get showOrganiserUserOptions():boolean{
    return this.usersService.userPermissions.isOrganiserUser;
  }
  get showInternalOrbitUserOptions():boolean{
    return (!this.showOrganiserUserOptions);
  }
  get showCustomerServicesOrAccountManagerOptions():boolean{
    return (this.usersService.userPermissions.isCustomerServicesUser || this.usersService.userPermissions.isAccountManager);
  }

  //#endregion
  constructor(
    protected usersService: UsersService,
    protected timeService: TimeService,
    private asideService: AsideService,
    private injector: Injector,
    private router: Router,
    private snackBar: SnackBarService,
  ) {
    router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: any) => {
        if (event.urlAfterRedirects.includes('/projects/')) {
          const urlSplitOnCustomers =
            event.urlAfterRedirects.split('customers');
          const urlAfterCustomers = urlSplitOnCustomers[1].split('/');
          this.customerId = +urlAfterCustomers[1];

          const urlSplitOnProjects = event.urlAfterRedirects.split('projects');
          const urlAfterProjects = urlSplitOnProjects[1].split('/');
          this.projectId = +urlAfterProjects[1];

          this.systemDesignVisible = true;
        } else {
          this.systemDesignVisible = false;
          this.customerId = null;
          this.projectId = null;
        }

        console.log(`url = ${event.url}`);
      });
  }

  public showTimeProvider = () => (this.timeModel ? this.timeModel.showTimeProvider : false);

  public currentDatetimeValue = () => (this.timeModel ? this.timeModel.currentDateTime : '');

  private refreshCurrentTime() {
    this.timeService.getCurrentTime().then((timeModel: TimeModel) => {
      this.timeModel = timeModel;
    });
  }

  ngOnInit() {
    this.appVersion = environment.buildLabel;

    this.refreshCurrentTime();
  }

  ngOnDestroy() {

  }
  

  ngOnChanges() {}

  setTimeProvider() {
    this.asideService.setComponent(new ComponentItem(TimeProviderCreateUpdateComponent, { itemId: 0 }));
    const that = this;
    this.asideService.asideClose.subscribe(() => {
      that.refreshCurrentTime();
    });
    this.asideService.open();
  }

  submitDefect() {
    this.asideService.setAndOpen(new ComponentItem(ExternalRaiseDefectFormComponent, {}), this.injector);
  }

  showNotImplementedFutureSprint(event: MouseEvent){
    this.snackBar.info('This functionality will be added in a future sprint.');
  }

  getSystemDesignRoute(button: number): string {
    const baseRoute = 'customers/' + this.customerId + '/projects/' + this.projectId + '/system-design/';

    switch (button) {
      case SystemDesignType.Meetings:
        return baseRoute + 'meetings';
      case SystemDesignType.Requirements:
        return baseRoute + 'requirements';
      case SystemDesignType.Definitions:
        return baseRoute + 'definitions';
      case SystemDesignType.DomainModel:
        return baseRoute + 'domain-model';
      case SystemDesignType.Rules:
        return baseRoute + 'rules';
      case SystemDesignType.ListSource:
        return baseRoute + 'list-sources';
      case SystemDesignType.Design:
        return baseRoute + 'design';
      default:
        console.debug('Unsupported route');
        return '';
    }
  }

  isOrdersActive(): boolean {
    const url = this.router.url;

    return url.startsWith('/orders') &&
           !url.startsWith('/orders/transactions') &&
           !url.startsWith('/orders/payments-refunds');
  }
  
  get devModeOnly(): boolean {
    return isDevMode();
  }

  get isDevOrTestMode(): boolean {
    var isTest = environment.name === 'TEST';
    return isDevMode() || isTest;
  }

}
